import React from 'react';
import {
    Card,
    CardContent,
    Box,
    Typography,
    Button,
    Divider,
    Chip
} from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import DownloadIcon from '@mui/icons-material/Download';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import VerifiedIcon from '@mui/icons-material/Verified';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PersonIcon from '@mui/icons-material/Person';
import { BADGE_TYPES } from '../data/badgeTypes';
import { buildLinkedInShareUrl } from '../utils/linkedinShare';
import { downloadBadgeImage } from './BadgeDownload';

// Accepts a `badge` prop — the DB document with shape:
// { _id, badgeTypeId, recipientName, recipientEmail, year, message, credentialJson, revoked, badgeImageUrl }
const BadgeCard = ({ badge, compact = false }) => {
    const badgeType = BADGE_TYPES[badge.badgeTypeId];

    if (!badgeType) return null;

    // Use custom uploaded image if available, otherwise fall back to default award type image
    const displayImage = badge.badgeImageUrl || badgeType.image;

    const credential = badge.credentialJson || {};
    const issuanceDate = credential.issuanceDate || badge.createdAt;
    const verificationUrl = credential.verificationUrl || '';

    const formattedDate = issuanceDate
        ? new Date(issuanceDate).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
        : badge.year;

    const linkedInUrl = buildLinkedInShareUrl({
        verificationUrl,
        badgeName: badgeType.name,
        issuerName: 'SDS Industry Consortium',
        issueDate: issuanceDate || new Date().toISOString(),
    });

    const handleDownload = () => {
        downloadBadgeImage(displayImage, `sdsic-${badge.badgeTypeId}-${badge.year}.png`);
    };

    return (
        <Card
            elevation={3}
            sx={{
                borderRadius: 3,
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                '&:hover': {
                    transform: 'translateY(-6px)',
                    boxShadow: `0 12px 24px ${badgeType.color}30`,
                }
            }}
        >
            {/* Badge Image */}
            <Box
                sx={{
                    p: 3,
                    backgroundColor: '#0a1628',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <Box
                    component="img"
                    src={displayImage}
                    alt={badgeType.name}
                    sx={{
                        width: '100%',
                        maxWidth: 280,
                        height: 'auto',
                        borderRadius: 2,
                    }}
                />
            </Box>

            <CardContent sx={{ flexGrow: 1, p: 3 }}>
                {/* Award Name */}
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 700,
                        color: badgeType.color,
                        mb: 1,
                        lineHeight: 1.3,
                    }}
                >
                    {badgeType.name}
                </Typography>

                <Chip
                    label={`SDSIC ${badge.year}`}
                    size="small"
                    sx={{
                        backgroundColor: badgeType.colorLight,
                        color: badgeType.color,
                        fontWeight: 700,
                        mb: 2,
                    }}
                />

                <Divider sx={{ my: 2 }} />

                {/* Credential Details */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <PersonIcon sx={{ fontSize: 20, color: '#666' }} />
                        <Box>
                            <Typography variant="caption" color="text.secondary">Issued to</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {badge.recipientName}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <CalendarMonthIcon sx={{ fontSize: 20, color: '#666' }} />
                        <Box>
                            <Typography variant="caption" color="text.secondary">Issued</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {formattedDate}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <VerifiedIcon sx={{ fontSize: 20, color: badge.revoked ? '#d32f2f' : '#4caf50' }} />
                        <Box>
                            <Typography variant="caption" color="text.secondary">Status</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: badge.revoked ? '#d32f2f' : '#4caf50' }}>
                                {badge.revoked ? 'Revoked' : 'Valid — No expiration'}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {badge.message && (
                    <Box sx={{ p: 2, backgroundColor: badgeType.colorLight, borderRadius: 2, mb: 2 }}>
                        <Typography variant="body2" sx={{ fontStyle: 'italic', color: badgeType.colorDark }}>
                            "{badge.message}"
                        </Typography>
                    </Box>
                )}

                <Divider sx={{ my: 2 }} />

                {/* QR Code & Actions */}
                {!compact && verificationUrl && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                        <QRCodeSVG
                            value={verificationUrl}
                            size={80}
                            level="M"
                            style={{ flexShrink: 0 }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', flex: 1, minWidth: 120 }}>
                            Scan to verify this credential on the SDSIC website
                        </Typography>
                    </Box>
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Button
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownload}
                        fullWidth
                        sx={{
                            backgroundColor: badgeType.color,
                            '&:hover': {
                                backgroundColor: badgeType.colorDark,
                            }
                        }}
                    >
                        Download Badge
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<LinkedInIcon />}
                        href={linkedInUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        fullWidth
                        sx={{
                            borderColor: '#0A66C2',
                            color: '#0A66C2',
                            '&:hover': {
                                borderColor: '#0A66C2',
                                backgroundColor: 'rgba(10, 102, 194, 0.08)',
                            }
                        }}
                    >
                        Add to LinkedIn
                    </Button>
                </Box>
            </CardContent>
        </Card>
    );
};

export default BadgeCard;
